// PortfolioPage.js
// Route: /dashboard/portfolio
// Uses the same data services as Right Report (ReportsTab):
//   - useUserRecommendationAccess  → Firestore recommendations for this user
//   - fetchLtpFromSheet            → Live LTP from Google Sheet
import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useUserRecommendationAccess } from '../../hooks/useUserRecommendationAccess';
import { fetchLtpFromSheet } from '../../Services/ltpSheetService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase';
import MetricsBar from './MetricsBar';
import PerformanceCharts from './PerformanceCharts';
import RecommendationsTable from './RecommendationsTable';
import ComparisonSummary from './ComparisonSummary';
import './Portfolio.css';

// ── Date helper (handles Firestore Timestamp, JS Date, ISO string) ────────────
const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
        let d;
        if (typeof dateValue === 'object' && dateValue.toDate) {
            d = dateValue.toDate();                // Firestore Timestamp
        } else if (dateValue instanceof Date) {
            d = dateValue;
        } else {
            d = new Date(dateValue);               // ISO string
        }
        return d.toISOString().split('T')[0];
    } catch {
        return '-';
    }
};

// ── Map one Firestore recommendation + live LTP map → trade object ────────────
// Field names mirror what ReportsTab already reads from Firestore.
const mapToTrade = (rec, ltpMap) => {
    const triggerPrice = parseFloat(rec.triggerPrice) || 0;
    const isClosed = (rec.status || '').toLowerCase() === 'closed';

    // Live LTP from Google Sheet (keyed by stockName)
    const ltpRaw = ltpMap[rec.stockName];
    const ltpValue = ltpRaw && !isNaN(parseFloat(ltpRaw)) ? parseFloat(ltpRaw) : null;

    // % change:
    //   closed  → use stored rec.percentChange  (same logic as ReportsTab)
    //   active  → calculate from live LTP if available
    let pct = 0;
    if (isClosed) {
        const stored = parseFloat(rec.percentChange);
        pct = isNaN(stored) ? 0 : stored;
    } else if (ltpValue !== null && triggerPrice !== 0) {
        pct = ((ltpValue - triggerPrice) / triggerPrice) * 100;
    }

    return {
        id: rec.id,
        stock: rec.stockName || '—',
        sector: rec.sector || '',          // sector field if present
        date: formatDate(rec.broadcastDate),
        action: rec.action ? rec.action.toUpperCase() : 'BUY',
        range: rec.buyingRange || '—',
        trigger: triggerPrice,
        ltp: isClosed ? null : ltpValue,     // blank LTP for closed (same as ReportsTab)
        pct: +pct.toFixed(2),
        target: parseFloat(rec.target) || null,
        sl: parseFloat(rec.stopLoss) || null,
        reward: rec.rewardPercent ? parseInt(rec.rewardPercent) : 0,
        risk: rec.riskPercent ? parseInt(rec.riskPercent) : 0,
        status: isClosed ? 'closed' : 'active',
    };
};

// ── calcStats — pure function, used by both portfolio metrics and comparison ───
export const calcStats = (trades, selectedIds) => {
    const sel = trades.filter((t) => selectedIds.includes(t.id));
    const closed = sel.filter((t) => t.status === 'closed');
    const active = sel.filter((t) => t.status === 'active');
    const winners = closed.filter((t) => t.pct > 0);
    const losers = closed.filter((t) => t.pct <= 0);

    const winRate = closed.length ? Math.round((winners.length / closed.length) * 100) : 0;
    const maxRoi = closed.length ? Math.max(...closed.map((t) => t.pct)) : 0;
    const avgRoiWin = winners.length
        ? winners.reduce((s, t) => s + t.pct, 0) / winners.length : 0;
    const avgLoss = losers.length
        ? losers.reduce((s, t) => s + t.pct, 0) / losers.length : 0;
    const maxWin = winners.length ? Math.max(...winners.map((t) => t.pct)) : 0;

    const portfolioAvg = closed.length
        ? +(closed.reduce((s, t) => s + t.pct, 0) / closed.length).toFixed(1)
        : 0;

    // Avg unrealised % gain across active trades (uses live LTP pct)
    const unrealisedGain = active.length
        ? +(active.reduce((s, t) => s + t.pct, 0) / active.length).toFixed(1)
        : null;

    // Avg % across ALL selected trades (closed + active)
    const totalAvg = sel.length
        ? +(sel.reduce((s, t) => s + t.pct, 0) / sel.length).toFixed(1)
        : 0;

    return {
        total: sel.length,
        active: active.length,
        closed: closed.length,
        winners: winners.length,
        losers: losers.length,
        winRate,
        maxRoi: +maxRoi.toFixed(1),
        avgRoiWin: +avgRoiWin.toFixed(1),
        avgLoss: +avgLoss.toFixed(1),
        maxWin: +maxWin.toFixed(1),
        portfolioAvg,
        unrealisedGain,
        totalAvg,
    };
};

// ── Page component ────────────────────────────────────────────────────────────
const PortfolioPage = () => {
    const { user } = useContext(AuthContext);

    // Same hooks as ReportsTab
    const {
        recommendations: rawRecs,
        loading: recsLoading,
        error: recsError,
    } = useUserRecommendationAccess();

    const [ltpMap, setLtpMap] = useState({});
    const [ltpLoading, setLtpLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [currentTab, setCurrentTab] = useState('all');
    const [niftyData, setNiftyData] = useState(null);
    const niftyFetched = useRef(false);

    // ── Fetch Nifty YTD once (shared by MetricsBar + ComparisonSummary) ──────
    const fetchNifty = () => {
        const getNiftyPrice = httpsCallable(functions, 'getNiftyPrice');
        getNiftyPrice()
            .then(({ data }) => { if (data?.currentPrice != null) setNiftyData(data); })
            .catch(() => { });
    };

    useEffect(() => {
        if (!user || niftyFetched.current) return;
        niftyFetched.current = true;
        fetchNifty();
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Fetch LTP from Google Sheet (same as ReportsTab effect 3) ────────────
    useEffect(() => {
        if (!rawRecs || rawRecs.length === 0) return;
        let isMounted = true;
        setLtpLoading(true);
        fetchLtpFromSheet()
            .then((map) => { if (isMounted) setLtpMap(map || {}); })
            .catch(() => { if (isMounted) setLtpMap({}); })
            .finally(() => { if (isMounted) setLtpLoading(false); });
        return () => { isMounted = false; };
    }, [rawRecs]);

    // ── Map Firestore recs → unified trade objects ────────────────────────────
    const trades = useMemo(
        () => (rawRecs || []).map((rec) => mapToTrade(rec, ltpMap)),
        [rawRecs, ltpMap]
    );

    // ── Initialise selection to all IDs when data first arrives ──────────────
    useEffect(() => {
        if (trades.length > 0 && selected.length === 0) {
            setSelected(trades.map((t) => t.id));
        }
    }, [trades]);

    // IDs of rows matching the active tab (ALL / ACTIVE / CLOSED).
    // Single source of truth for the summary scope, the comparison baseline,
    // and the header "select-all" checkbox.
    const tabFilteredIds = useMemo(
        () => trades
            .filter((t) => {
                if (currentTab === 'active') return t.status === 'active';
                if (currentTab === 'closed') return t.status === 'closed';
                return true;
            })
            .map((t) => t.id),
        [trades, currentTab]
    );

    // Effective selection = checkbox selection ∩ active tab.
    // Switching tabs now recalculates every summary widget, exactly like (un)checking a row.
    const effectiveSelected = useMemo(
        () => selected.filter((id) => tabFilteredIds.includes(id)),
        [selected, tabFilteredIds]
    );

    const selectedStats = useMemo(() => calcStats(trades, effectiveSelected), [trades, effectiveSelected]);
    // "All Recommendations" stays a fixed all-trades baseline regardless of the active tab (Option B).
    const allIds = useMemo(() => trades.map((t) => t.id), [trades]);
    const allStats = useMemo(() => calcStats(trades, allIds), [trades, allIds]);

    const toggleTrade = (id) =>
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );

    const toggleAll = () => {
        const allVisible =
            tabFilteredIds.length > 0 && tabFilteredIds.every((id) => selected.includes(id));
        if (allVisible) {
            setSelected((prev) => prev.filter((id) => !tabFilteredIds.includes(id)));
        } else {
            setSelected((prev) => [...new Set([...prev, ...tabFilteredIds])]);
        }
    };

    const isLoading = recsLoading || ltpLoading;

    // ── Not signed in ─────────────────────────────────────────────────────────
    if (!user) {
        return (
            <div className="portfolio-page">
                <div className="wrap-empty">
                    <p>Please sign in to view your portfolio.</p>
                </div>
            </div>
        );
    }

    // ── Premium / access check — mirrors ReportsTab guard ────────────────────
    const hasPremiumAccess = user?.role === 'premium' || user?.role === 'admin';
    if (!hasPremiumAccess) {
        return (
            <div className="portfolio-page">
                <div className="portfolio-header">
                    <div>
                        <h1 className="portfolio-title">My Portfolio</h1>
                        <p className="portfolio-subtitle">Track and analyse your RightSpade recommendation performance.</p>
                    </div>
                </div>
                <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: '10px', padding: '2rem', textAlign: 'center',
                    color: '#1d4ed8', fontSize: '0.92rem',
                }}>
                    🔐 Access restricted to subscribers. Please upgrade your plan to view this content.
                </div>
            </div>
        );
    }

    return (
        <div className="portfolio-page">

            {/* ── Page header ─────────────────────────────────────────────── */}
            <div className="portfolio-header">
                <div>
                    <h1 className="portfolio-title">My Portfolio</h1>
                    <p className="portfolio-subtitle">
                        Track and analyse your RightSpade recommendation performance.
                        Switch between All / Active / Closed or uncheck any trade you didn't take —
                        every metric updates instantly.
                    </p>
                </div>
                {isLoading && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        color: '#6c757d', fontSize: '0.82rem',
                    }}>
                        <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{ color: '#3871E0' }}
                        />
                        Loading…
                    </div>
                )}
            </div>

            {/* ── Error banner ─────────────────────────────────────────────── */}
            {recsError && recsError !== 'User not authenticated' && (
                <div style={{
                    marginBottom: '1rem', padding: '0.75rem 1rem',
                    background: '#fef2f2', border: '1px solid #fecaca',
                    borderRadius: '8px', color: '#b91c1c', fontSize: '0.85rem',
                }}>
                    ⚠ Error loading recommendations: {recsError}
                </div>
            )}

            {/* ── Empty state ──────────────────────────────────────────────── */}
            {!recsLoading && !recsError && trades.length === 0 && (
                <div className="wrap-empty">
                    <p>No recommendations available for your account yet.</p>
                    <p style={{ fontSize: '0.82rem', marginTop: '0.5rem', color: '#6b7280' }}>
                        Recommendations are assigned based on your subscription plan.
                    </p>
                </div>
            )}

            {/* ── Main dashboard — rendered when data is ready ─────────────── */}
            {trades.length > 0 && (
                <>
                    {/* KPI metrics */}
                    <p className="port-section-label">Portfolio Summary</p>
                    <MetricsBar stats={selectedStats} niftyData={niftyData} />

                    {/* Performance charts */}
                    <PerformanceCharts
                        trades={trades}
                        selected={effectiveSelected}
                        stats={selectedStats}
                    />

                    {/* Recommendations table */}
                    <p className="port-section-label">Recommendations</p>
                    <RecommendationsTable
                        trades={trades}
                        selected={selected}
                        currentTab={currentTab}
                        onTabChange={setCurrentTab}
                        onToggle={toggleTrade}
                        onToggleAll={toggleAll}
                    />

                    {/* Comparison summary */}
                    <p className="port-section-label" style={{ marginTop: '0.25rem' }}>
                        My Portfolio vs All Trades — Comparison Summary
                    </p>
                    <ComparisonSummary
                        allStats={allStats}
                        selectedStats={selectedStats}
                        niftyData={niftyData}
                        onRefreshNifty={fetchNifty}
                    />

                    {/* Disclaimer */}
                    <p className="portfolio-disclaimer">
                        This content is compiled from RightSpade’s published research reports and is for informational and educational purposes only. Performance shown is based on model recommendations using trigger levels and tracked market prices; actual investor returns may vary materially depending on execution price, timing, allocation, and individual decisions. Past performance is not indicative of future results and does not constitute investment advice or a recommendation to buy or sell any securities.
                        <br /><br />
                        Right Spade Capital Advisors Pvt. Ltd. is a SEBI Registered Research Analyst
                        (INH000018799). Please consult a qualified financial advisor before making any
                        investment decisions. Kindly refer to our{' '}
                        <Link to="/legal#news-policy">News &amp; Research Disclaimer Policy</Link> for details.
                    </p>
                </>
            )}
        </div>
    );
};

export default PortfolioPage;
